import type { Outcome } from "@landing/types"

export interface DominantConfig {
  slug: string
  min: number
  max: number
}

export interface TradeUpdate {
  type: "buy" | "sell"
  timestamp: number
}

// Constants for price constraints
export const PRICE_CONSTRAINTS = {
  MIN_CLAMP: 5,
  MAX_CLAMP: 25,
  MIN_PRICE: 1,
  TARGET_TOTAL: 100,
} as const

// Calculate volatility based on seed number
export function calculateVolatility(seed?: number): number {
  const seedValue = seed ?? 5
  return 1 + (9 - seedValue) * 0.35
}

// Parse price string to number
export function parsePrice(price: string): number {
  return Number.parseFloat(price)
}

// Format price to string with 2 decimal places
export function formatPrice(price: number): string {
  return price.toFixed(2)
}

// Generate random change with momentum
export function generatePriceChange(
  currentMomentum: number,
  volatility: number,
  newsEvent?: { index: number; change: number } | null,
  outcomeIndex?: number,
): number {
  const baseChange = (Math.random() * 7 - 3.5) * volatility
  const momentumInfluence = currentMomentum * 0.45
  let change = baseChange + momentumInfluence

  // Apply news spike if applicable
  if (newsEvent && outcomeIndex === newsEvent.index) {
    change += newsEvent.change
  }

  return Number.parseFloat(change.toFixed(2))
}

// Update momentum with decay
export function updateMomentum(currentMomentum: number, change: number): number {
  return currentMomentum * 0.7 + change * 0.3
}

// Generate news event occasionally
export function generateNewsEvent(outcomeCount: number): { index: number; change: number } | null {
  if (Math.random() < 0.07) {
    return {
      index: Math.floor(Math.random() * outcomeCount),
      change: Number((Math.random() * 17.5 - 7.5).toFixed(2)),
    }
  }
  return null
}

// Normalize outcomes to sum to target total
export function normalizeOutcomes(outcomes: Outcome[], targetTotal = PRICE_CONSTRAINTS.TARGET_TOTAL): Outcome[] {
  const total = outcomes.reduce((sum, o) => sum + parsePrice(o.price), 0)
  if (total <= 0) return outcomes

  return outcomes.map((o) => ({
    ...o,
    price: formatPrice((parsePrice(o.price) * targetTotal) / total),
  }))
}

// Apply dominant configurations to specific outcomes
export function applyDominantConfigs(outcomes: Outcome[], dominantConfigs: DominantConfig[]): Outcome[] {
  if (!dominantConfigs || dominantConfigs.length === 0) return outcomes

  const newOutcomes = [...outcomes]
  let dominantSum = 0
  const dominantIndices: number[] = []

  // Set prices for dominant outcomes
  dominantConfigs.forEach((cfg, i) => {
    const idx = newOutcomes.findIndex((o) => o.slug === cfg.slug)
    if (idx === -1) return

    const outcome = newOutcomes[idx]
    if (!outcome) return

    dominantIndices.push(idx)

    // Deterministic target based on position
    let target: number
    if (i === 0) {
      target = cfg.max - 1
    } else if (i === 1) {
      target = cfg.max - 5
    } else {
      target = (cfg.min + cfg.max) / 2
    }

    target = Math.min(cfg.max, Math.max(cfg.min, target))
    outcome.price = formatPrice(target)
    dominantSum += target
  })

  // Re-scale other outcomes
  const otherIndices = newOutcomes.map((_, i) => i).filter((i) => !dominantIndices.includes(i))
  const otherSumCurrent = otherIndices.reduce((sum, i) => {
    const outcome = newOutcomes[i]
    return outcome ? sum + parsePrice(outcome.price) : sum
  }, 0)

  if (otherSumCurrent > 0) {
    const remaining = PRICE_CONSTRAINTS.TARGET_TOTAL - dominantSum
    const scale = Math.max(remaining / otherSumCurrent, 0)

    for (const i of otherIndices) {
      const outcome = newOutcomes[i]
      if (!outcome) continue

      const price = parsePrice(outcome.price) * scale
      outcome.price = formatPrice(price)
    }
  }

  return normalizeOutcomes(newOutcomes)
}

// Clamp prices within bounds and normalize
export function clampAndNormalize(outcomes: Outcome[]): Outcome[] {
  const n = outcomes.length
  let min = PRICE_CONSTRAINTS.MIN_CLAMP
  let max = PRICE_CONSTRAINTS.MAX_CLAMP

  // Adjust bounds if mathematically impossible
  if (n * min > PRICE_CONSTRAINTS.TARGET_TOTAL) {
    min = PRICE_CONSTRAINTS.TARGET_TOTAL / n
  }
  if (n * max < PRICE_CONSTRAINTS.TARGET_TOTAL) {
    max = PRICE_CONSTRAINTS.TARGET_TOTAL / n
  }

  // Initial clamping
  const prices = outcomes.map((o) => {
    const p = parsePrice(o.price)
    return Math.min(max, Math.max(min, p))
  })

  // Adjust to reach target total
  let guard = 0
  while (Math.abs(prices.reduce((a, b) => a + b, 0) - PRICE_CONSTRAINTS.TARGET_TOTAL) > 0.01 && guard < 10) {
    const sum = prices.reduce((a, b) => a + b, 0)
    const diff = PRICE_CONSTRAINTS.TARGET_TOTAL - sum

    if (diff > 0) {
      // Distribute to outcomes below max
      const candidates = prices.map((p, i) => ({ p, i })).filter((c) => c.p < max)
      if (candidates.length === 0) break

      const add = diff / candidates.length
      for (const candidate of candidates) {
        const currentPrice = prices[candidate.i]
        if (currentPrice !== undefined) {
          prices[candidate.i] = Math.min(max, currentPrice + add)
        }
      }
    } else {
      // Subtract from outcomes above min
      const candidates = prices.map((p, i) => ({ p, i })).filter((c) => c.p > min)
      if (candidates.length === 0) break

      const sub = Math.abs(diff) / candidates.length
      for (const candidate of candidates) {
        const currentPrice = prices[candidate.i]
        if (currentPrice !== undefined) {
          prices[candidate.i] = Math.max(min, currentPrice - sub)
        }
      }
    }
    guard++
  }

  return outcomes.map((o, idx) => {
    const price = prices[idx]
    return {
      ...o,
      price: formatPrice(price ?? 0),
    }
  })
}

// Sort outcomes by price and state
export function sortOutcomes(outcomes: Outcome[]): Outcome[] {
  return outcomes.sort((a, b) => {
    if (a.state === "eliminated" && b.state !== "eliminated") return 1
    if (a.state !== "eliminated" && b.state === "eliminated") return -1
    return parsePrice(b.price) - parsePrice(a.price)
  })
}
